import PropTypes from 'prop-types';
import React from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { BsInfoCircleFill } from 'react-icons/bs';
import { MdAdd, MdOutlineRemoveCircleOutline } from 'react-icons/md';
import Tooltip from '@/components/tooltip/tooltip-component';
import Styled from './image-gallery-component-style';

/**
 * ImageGallery component to display and manage a collection of images.
 * @param {object} props The props object.
 * @param {string} props.name The name of the input field.
 * @param {string} props.margin The margin of the container.
 * @param {Function} props.onChange The onChange callback function.
 * @param {string} props.description The description of the image gallery.
 * @returns {JSX.Element} The ImageGallery component.
 */
const ImageGallery = ({ name, margin, onChange, description }) => {
  const { control } = useFormContext();

  const { fields, remove, insert } = useFieldArray({
    control,
    name, // unique name for your Field Array
    keyName: 'key',
  });

  // Create unique ID based on field name to avoid conflicts when multiple galleries are on the page
  const fileInputId = `imageInputForGallery-${name.replace(/\./g, '-')}`;

  const handleImageChange = (event) => {
    const uploadedFiles = event.target.files;
    const files = Array.from(uploadedFiles);

    files
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((file, index) => {
        insert(fields.length + index, {
          file,
        });
      });

    // Call onChange callback if provided to track changes
    if (onChange) {
      const allFiles = [...fields, ...files.map((file) => ({ file }))];
      onChange(allFiles);
    }
  };

  const fieldImages = fields.map((field) =>
    field.image
      ? {
          path: field.image, // old images
          name: field.id,
        }
      : {
          path: URL.createObjectURL(field.file), // new images
          name: field.file.name,
        },
  );

  const handleRemoveImage = (event, index) => {
    event.preventDefault();
    remove(index);

    // Call onChange callback if provided to track changes
    if (onChange) {
      const updatedFiles = fields.filter((_, i) => i !== index);
      onChange(updatedFiles);
    }
  };

  return (
    <>
      <Styled.HiddenInput
        type="file"
        id={fileInputId}
        multiple
        onChange={handleImageChange}
        accept=".jpeg, .jpg, .png"
        hidden
      />
      <Styled.Container $margin={margin}>
        {fieldImages.map((file, index) => (
          <Styled.ImageWrapper key={`image-item + ${index + 1}`}>
            <Styled.Image src={file?.path} alt={file?.name} />
            <Styled.RemoveButton onClick={(e) => handleRemoveImage(e, index)}>
              <MdOutlineRemoveCircleOutline />
            </Styled.RemoveButton>
          </Styled.ImageWrapper>
        ))}
      </Styled.Container>
      <Styled.Footer>
        <Styled.AddImageButton htmlFor={fileInputId}>
          <MdAdd /> {description}
        </Styled.AddImageButton>
        <Tooltip
          id="categoryTooltip"
          text="For best results, upload JPEG or PNG files that are 1280 X 720 pixels or larger. Max upload size is 2MB."
          icon={<BsInfoCircleFill />}
        />
      </Styled.Footer>
    </>
  );
};

ImageGallery.defaultProps = {
  margin: '0',
  onChange: null,
  description: 'Add Product Gallery Images',
};

ImageGallery.propTypes = {
  name: PropTypes.string.isRequired,
  margin: PropTypes.string,
  onChange: PropTypes.func,
  description: PropTypes.string,
};

/**
 *
 * @param {object} props - The props object.
 * @param {string} props.margin - The margin of the container.
 * @param {Array} props.images - The images to display.
 * @returns {JSX.Element} The ImageGalleryView component.
 */
// Component for displaying images without form context
const SimpleImageGalleryView = ({ margin, images }) => {
  const fieldImages =
    images
      ?.map((field) =>
        field.image
          ? {
              path: field.image, // old images
              name: field.id,
            }
          : undefined,
      )
      .filter(Boolean) ?? [];

  return (
    <Styled.Container $margin={margin}>
      {fieldImages?.map((file, index) => (
        <Styled.ImageWrapper key={`image-item + ${index + 1}`}>
          <Styled.Image src={file?.path} alt={file?.name} />
        </Styled.ImageWrapper>
      ))}
    </Styled.Container>
  );
};

SimpleImageGalleryView.defaultProps = {
  margin: '0',
  images: [],
};

SimpleImageGalleryView.propTypes = {
  margin: PropTypes.string,
  images: PropTypes.array,
};

/**
 *
 * @param {object} props - The props object.
 * @param {string} props.name - The name of the input field.
 * @param {string} props.margin - The margin of the container.
 * @returns {JSX.Element} The FormImageGalleryView component.
 */
const FormImageGalleryView = ({ name, margin }) => {
  const { control } = useFormContext();

  const { fields } = useFieldArray({
    control,
    name, // unique name for your Field Array
  });

  const fieldImages =
    fields.map((field) =>
      field.image
        ? {
            path: field.image, // old images
            name: field.id,
          }
        : undefined,
    ) ?? [];

  return (
    <Styled.Container $margin={margin}>
      {fieldImages?.map((file, index) => (
        <Styled.ImageWrapper key={`image-item + ${index + 1}`}>
          <Styled.Image src={file?.path} alt={file?.name} />
        </Styled.ImageWrapper>
      ))}
    </Styled.Container>
  );
};

FormImageGalleryView.defaultProps = {
  margin: '0',
  name: null,
};

FormImageGalleryView.propTypes = {
  name: PropTypes.string,
  margin: PropTypes.string,
};

export { SimpleImageGalleryView, FormImageGalleryView };

export default ImageGallery;
